import { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import { keyframes } from "@mui/system";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";

import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import SvgIcon from "@mui/material/SvgIcon";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";

import AnswerText from "./AnswerText";
import SourcesDialog from "./SourcesDialog";
import ContinueConversationInput from "./ContinueConversationInput";

const AIOverviewIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 18 18">
        <path d="M10.5 3.32812C11.5097 3.32812 12.3281 2.50964 12.3281 1.5H13.1719C13.1719 2.50964 13.9903 3.32812 15 3.32812V4.17188C13.9903 4.17188 13.1719 4.99036 13.1719 6H12.3281C12.3281 4.99036 11.5097 4.17188 10.5 4.17188V3.32812ZM0.75 8.25C3.23528 8.25 5.25 6.23528 5.25 3.75H6.75C6.75 6.23528 8.76472 8.25 11.25 8.25V9.75C8.76472 9.75 6.75 11.7647 6.75 14.25H5.25C5.25 11.7647 3.23528 9.75 0.75 9.75V8.25ZM3.65701 9C4.64038 9.5457 5.45432 10.3596 6 11.343C6.54568 10.3596 7.35962 9.5457 8.343 9C7.35962 8.4543 6.54568 7.6404 6 6.65701C5.45432 7.6404 4.64038 8.4543 3.65701 9ZM12.9375 10.5C12.9375 11.8462 11.8462 12.9375 10.5 12.9375V14.0625C11.8462 14.0625 12.9375 15.1538 12.9375 16.5H14.0625C14.0625 15.1538 15.1538 14.0625 16.5 14.0625V12.9375C15.1538 12.9375 14.0625 11.8462 14.0625 10.5H12.9375Z" fill="currentColor" />
    </SvgIcon>
);

/** Convert markdown to plain text (no ** or other syntax) for plain-text paste. */
function markdownToPlainText(md) {
    if (!md || typeof md !== "string") return "";
    return md
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/__(.+?)__/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/_(.+?)_/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/^#+\s*/gm, "")
        .trim();
}

/** Convert markdown to HTML so copy preserves bold/italic when pasting into rich-text editors. */
function markdownToHtml(md) {
    if (!md || typeof md !== "string") return "";
    const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const wrapped = escape(md)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.+?)__/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/_(.+?)_/g, "<em>$1</em>")
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/^#+\s*(.*)$/gm, "<strong>$1</strong>");
    return `<p>${wrapped.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;
}

/** Copy markdown to clipboard as both plain text and HTML so bold/italic are preserved on paste. */
function copyMarkdownToClipboard(md) {
    const plain = markdownToPlainText(md);
    const html = markdownToHtml(md);
    const item = new ClipboardItem({
        "text/plain": new Blob([plain], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
    });
    return navigator.clipboard.write([item]).catch(() => navigator.clipboard.writeText(plain));
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* Figma shimmer gradient for skeleton bars */
const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const shimmerBg = {
    background:
        "linear-gradient(90deg, #f1efef 24.18%, #f9f8f8 50.26%, #e7e5e5 114.83%)",
    backgroundSize: "200% 100%",
    animation: `${shimmer} 1.8s ease-in-out infinite`,
    borderRadius: "6px",
};

/* Shared style for action buttons — Figma: 40×40px, border #ebe9e5, slightly rounded */
const circleButtonSx = {
    width: 40,
    height: 40,
    border: "1px solid #ebe9e5",
    borderRadius: "50%",
    color: "#3d3a37",
    backgroundColor: "transparent",
    "&:hover": {
        backgroundColor: "#F3F4F6",
        borderColor: "#d1cdc7",
    },
    "&.Mui-disabled": {
        color: "#D1D5DB",
        borderColor: "#E5E7EB",
        backgroundColor: "transparent",
    },
};

const LOADING_MESSAGES = [
    "AI is thinking...",
    "AI is checking articles...",
    "AI is answering...",
];

/** Cycles through loading messages with 1-second pauses. */
function LoadingText() {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (index >= LOADING_MESSAGES.length - 1) return;
        const timer = setTimeout(() => {
            setIndex((prev) => prev + 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [index]);

    return LOADING_MESSAGES[index];
}

/**
 * AI Summary answer card — matches Figma design (node 139-2351 / 139-2361).
 *
 * Two-tone card:
 *   - Gray top section (#f7f7f7) — "Fetching your answer…" header
 *   - White bottom section — query bubble, answer text, actions
 *
 * Border: rgba(0,0,0,0.1), border-radius: 20px.
 */
export default function AIAnswerCard({
    query,
    displayedText,
    isLoading,
    sources,
    followUps,
    onFollowUp,
    isFollowUpLoading,
    disableFollowUp = false,
    showSources = true,
    showContinueConversation = true,
}) {
    const [copiedOpen, setCopiedOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [scrollMaxHeight, setScrollMaxHeight] = useState(null);
    const scrollContentRef = useRef(null);

    const showSkeleton = isLoading && displayedText.length === 0;
    const isComplete = !isLoading && displayedText.length > 0;

    useEffect(() => {
        if (!showContinueConversation) {
            setScrollMaxHeight(null);
            return;
        }
        if (followUps.length < 1) {
            setScrollMaxHeight(null);
            return;
        }
        const lastComplete = followUps[followUps.length - 1] && !followUps[followUps.length - 1].isLoading;
        if (scrollContentRef.current && scrollMaxHeight === null && lastComplete) {
            setScrollMaxHeight(scrollContentRef.current.offsetHeight);
        }
    }, [showContinueConversation, followUps.length, followUps, scrollMaxHeight]);

    // Auto-scroll to bottom when new follow-up is added or latest answer is loading
    const lastFollowUpLength = followUps.length > 0 ? followUps[followUps.length - 1].displayedText.length : 0;
    useEffect(() => {
        if (!showContinueConversation) return;
        if (followUps.length < 1) return;
        const el = scrollContentRef.current;
        if (!el || el.scrollHeight <= el.clientHeight) return;
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight - el.clientHeight;
        });
    }, [showContinueConversation, followUps.length, lastFollowUpLength]);

    const handleCopy = () => {
        copyMarkdownToClipboard(displayedText).then(() => {
            setCopiedOpen(true);
        });
    };

    return (
        <Box
            sx={{
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: "20px",
                overflow: "hidden",
                mb: 4,
                transition: "box-shadow 0.3s ease",
                "&:hover": {
                    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                },
            }}
        >
            {/* ──────────────────────────────────────────────────────────
                 TOP SECTION: gray bg — "Fetching your answer…" header
                 ────────────────────────────────────────────────────────── */}
            <Box
                sx={{
                    backgroundColor: "#f7f7f7",
                    px: "26px",
                    py: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                }}
            >
                <AIOverviewIcon
                    sx={{
                        fontSize: 18,
                        color: "#475569",
                    }}
                />
                <Typography
                    sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: 14,
                        color: "#475569",
                    }}
                >
                    {isLoading
                        ? <LoadingText />
                        : "AI Summary"}
                </Typography>
            </Box>

            {/* ──────────────────────────────────────────────────────────
                 BOTTOM SECTION: white bg — query, answer, actions
                 ────────────────────────────────────────────────────────── */}
            <Box sx={{ backgroundColor: "#fff", position: "relative" }}>
                {/* ── Answer text — collapses to 196px (query ~100 + answer 96) ── */}
                <Collapse in={!collapsed} collapsedSize={196} timeout={350}>
                    <Box
                        ref={scrollContentRef}
                        sx={{
                            ...(showContinueConversation && followUps.length >= 1 && !collapsed && {
                                maxHeight: scrollMaxHeight != null ? scrollMaxHeight : undefined,
                                overflowY: "auto",
                            }),
                        }}
                    >
                        {/* ── Query bubble (Figma 206:4521 / 206:4296) ── */}
                        <Box sx={{ px: "26px", pt: "24px", pb: "26px" }}>
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "flex-start",
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "0px 32px 32px 32px",
                                    px: "20px",
                                    py: "14px",
                                    maxWidth: 700,
                                    maxHeight: 260,
                                    overflow: "hidden",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontWeight: 500,
                                        fontSize: 16,
                                        color: "#3d3a37",
                                        lineHeight: "24px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 9,
                                        WebkitBoxOrient: "vertical",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {query}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ px: "26px", pb: "24px" }}>
                            {showSkeleton && (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                    <Box sx={{ width: "100%", height: 24, ...shimmerBg }} />
                                    <Box sx={{ width: "81%", height: 24, ...shimmerBg }} />
                                    <Box sx={{ width: "92%", height: 24, ...shimmerBg }} />
                                    <Stack direction="row" spacing="10px">
                                        {[0, 1].map((i) => (
                                            <Box key={i} sx={{ width: 20, height: 20, borderRadius: "6px", ...shimmerBg }} />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            {displayedText.length > 0 && (
                                <AnswerText
                                    text={displayedText}
                                    showCursor={isLoading}
                                    sources={showSources ? sources : []}
                                />
                            )}
                            {/* ── Action buttons for the first answer ── */}
                            {!showSkeleton && displayedText.length > 0 && (
                                <Box sx={{ mt: "16px" }}>
                                    <Stack direction="row" spacing="10px" alignItems="center">
                                        <Tooltip title="Copy answer">
                                            <span>
                                                <IconButton size="small" disabled={!isComplete} onClick={handleCopy} sx={circleButtonSx}>
                                                    <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {showSources && (
                                            <SourcesDialog
                                                sources={sources}
                                                disabled={!isComplete}
                                                renderTrigger={(onClick) => (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 14 }} />}
                                                        disabled={!isComplete}
                                                        onClick={onClick}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontFamily: "'Inter', sans-serif",
                                                            fontSize: 14,
                                                            fontWeight: 500,
                                                            lineHeight: "20px",
                                                            color: "#3d3a37",
                                                            borderColor: "#ebe9e5",
                                                            borderRadius: "999px",
                                                            px: "16px",
                                                            height: 40,
                                                            backgroundColor: "transparent",
                                                            gap: "8px",
                                                            "&:hover": { backgroundColor: "#F3F4F6", borderColor: "#d1cdc7" },
                                                            "&.Mui-disabled": { color: "#D1D5DB", borderColor: "#E5E7EB" },
                                                        }}
                                                    >
                                                        Sources ({sources?.length || 0})
                                                    </Button>
                                                )}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            )}
                        </Box>

                        {/* ── Follow-up conversations (only when expanded) ── */}
                        {showContinueConversation && followUps.map((item, idx) => {
                            const followUpComplete = !item.isLoading && item.displayedText.length > 0;
                            return (
                                <Box key={idx} sx={{ px: "26px", mt: "24px" }}>
                                    <Box sx={{ mb: "26px" }}>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "flex-start",
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: "0px 32px 32px 32px",
                                                px: "20px",
                                                py: "14px",
                                                maxWidth: 700,
                                                maxHeight: 260,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontWeight: 500,
                                                    fontSize: 16,
                                                    color: "#3d3a37",
                                                    lineHeight: "24px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 9,
                                                    WebkitBoxOrient: "vertical",
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {item.question}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {item.isLoading && item.displayedText.length === 0 && (
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", pb: "26px" }}>
                                            <Box sx={{ width: "100%", height: 24, ...shimmerBg }} />
                                            <Box sx={{ width: "81%", height: 24, ...shimmerBg }} />
                                            <Box sx={{ width: "92%", height: 24, ...shimmerBg }} />
                                            <Stack direction="row" spacing="10px">
                                                {[0, 1].map((i) => (
                                                    <Box key={i} sx={{ width: 20, height: 20, borderRadius: "6px", ...shimmerBg }} />
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                    {item.displayedText.length > 0 && (
                                        <AnswerText
                                            text={item.displayedText}
                                            showCursor={item.isLoading}
                                            sources={showSources ? sources : []}
                                        />
                                    )}
                                    {item.displayedText.length > 0 && (
                                        <Box sx={{ mt: "16px", pb: "26px" }}>
                                            <Stack direction="row" spacing="8px" alignItems="center">
                                                <Tooltip title="Copy answer">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={!followUpComplete}
                                                            onClick={() => copyMarkdownToClipboard(item.displayedText)}
                                                            sx={circleButtonSx}
                                                        >
                                                            <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {showSources && (
                                                    <SourcesDialog
                                                        sources={sources}
                                                        disabled={!followUpComplete}
                                                        renderTrigger={(onClick) => (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 14 }} />}
                                                                disabled={!followUpComplete}
                                                                onClick={onClick}
                                                                sx={{
                                                                    textTransform: "none",
                                                                    fontFamily: "'Inter', sans-serif",
                                                                    fontSize: 14,
                                                                    fontWeight: 500,
                                                                    lineHeight: "20px",
                                                                    color: "#3d3a37",
                                                                    borderColor: "#ebe9e5",
                                                                    borderRadius: "999px",
                                                                    px: "16px",
                                                                    height: 40,
                                                                    backgroundColor: "transparent",
                                                                    gap: "8px",
                                                                    "&:hover": { backgroundColor: "#F3F4F6", borderColor: "#d1cdc7" },
                                                                    "&.Mui-disabled": { color: "#D1D5DB", borderColor: "#E5E7EB" },
                                                                }}
                                                            >
                                                                Sources ({sources?.length || 0})
                                                            </Button>
                                                        )}
                                                    />
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}

                        {/* ── Continue conversation input — always in DOM, Collapse clips it when collapsed ── */}
                        {showContinueConversation && isComplete && (
                            <Box
                                sx={{
                                    px: "26px",
                                    pb: "24px",
                                    opacity: collapsed ? 0 : 1,
                                    transition: "opacity 0.2s ease",
                                }}
                            >
                                <ContinueConversationInput
                                    onSubmit={onFollowUp}
                                    disabled={isFollowUpLoading || collapsed || disableFollowUp}
                                />
                            </Box>
                        )}
                    </Box>
                </Collapse>

                {/* ── Gradient fade overlay when collapsed (Figma 139:6121 imgFooter) ── */}
                {isComplete && collapsed && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 120,
                            background:
                                "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 15%, rgba(255,255,255,0.85) 45%, rgba(255,255,255,1) 70%)",
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    />
                )}

                {/* ── Show full overview / Show less toggle (Figma 139:6121) ── */}
                {isComplete && (
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            px: "14px",
                            pt: "4px",
                            pb: "12px",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                        }}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                height: 40,
                                px: "12px",
                                py: "6px",
                                borderRadius: "6px",
                                userSelect: "none",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    lineHeight: "20px",
                                    color: "#3d3a37",
                                }}
                            >
                                {collapsed ? "Show full overview" : "Show less"}
                            </Typography>
                            {collapsed ? (
                                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, color: "#3d3a37" }} />
                            ) : (
                                <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14, color: "#3d3a37" }} />
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Copied snackbar */}
            <Snackbar
                open={copiedOpen}
                autoHideDuration={2000}
                onClose={() => setCopiedOpen(false)}
                message="Copied to clipboard"
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Box >
    );
}
